import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { authService } from '@/services/auth';
import { useAppErrorToast } from '@/hooks/use-app-error-toast';
import { useToast } from '@/hooks/use-toast';
import { applyFormErrorDetails } from '@/lib/form-errors';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AuthBackground } from '@/components/AuthBackground';
import { PasswordRules } from '@/components/PasswordRules';

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, 'Senha deve ter pelo menos 8 caracteres')
    .max(16, 'Senha deve ter no máximo 16 caracteres')
    .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
    .regex(/[0-9]/, 'Senha deve conter pelo menos um número')
    .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial'),
  confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não coincidem',
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const PASSWORD_RULES = {
  '[A-Z]': 'Pelo menos uma letra maiúscula',
  '[0-9]': 'Pelo menos um número',
  '.{8,}': 'Pelo menos 8 caracteres',
  '.{0,16}': 'Máximo 16 caracteres',
  '[^A-Za-z0-9]': 'Pelo menos um caractere especial',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const { showErrorToast } = useAppErrorToast();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const password = watch('password');

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setIsSubmitting(true);
      await authService.resetPassword({
        token,
        newPassword: data.password,
        passwordConfirmation: data.confirmPassword,
      });
      toast({
        variant: 'success',
        title: 'Senha redefinida',
        description: 'Sua senha foi atualizada com sucesso.',
      });
      navigate('/login');
    } catch (error: unknown) {
      const normalizedError = showErrorToast(error, {
        title: 'Não foi possível redefinir a senha',
        fallbackMessage: 'O link pode ter expirado. Solicite um novo link.',
      });

      applyFormErrorDetails(normalizedError.details, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-highlight flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      <AuthBackground />
      <div className="relative z-10">
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 w-full sm:w-96 flex flex-col gap-4">
          <div className="text-center mb-2">
            <h1 className="text-xl font-sans font-semibold text-gray-900">
              Definir nova senha
            </h1>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-1">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                {...register('password')}
                placeholder="Digite sua senha"
                error={errors.password?.message}
              />
              <PasswordRules password={password} rules={PASSWORD_RULES} />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-1">
                Confirme sua Senha
              </label>
              <Input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword')}
                placeholder="Digite sua senha"
                error={errors.confirmPassword?.message}
              />
            </div>
            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {isSubmitting ? 'Redefinindo...' : 'Redefinir senha'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
